import { useCallback, useState } from 'react';
import {
  DataCollectionEmailPreferences,
  EmailTemplateMessage,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { DataCollectionTemplateDefaultMessages } from '../../../services/queries/KPIRequestsQueries';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';

export function useDataCollectionConfigActions() {
  const [config, setConfig] = useState<DataCollectionEmailPreferences>(() => {
    return (
      PreferencesServiceV3.get().getPreference<DataCollectionEmailPreferences>(
        PreferenceKey.dataCollectionEmail,
        PreferenceScope.ORGANIZATION
      ) ?? {}
    );
  });

  const saveConfig = useCallback(
    async (newConfig: DataCollectionEmailPreferences) => {
      const updatedConfig = await PreferencesServiceV3.get().setPreference<DataCollectionEmailPreferences>(
        PreferenceKey.dataCollectionEmail,
        { ...config, ...newConfig },
        PreferenceScope.ORGANIZATION
      );
      setConfig(updatedConfig.value as DataCollectionEmailPreferences);
    },
    [config]
  );

  const resetConfig = useCallback(async () => {
    const defaultConfig = { ...config, ...getDefaultFormConfig() };
    const updatedConfig = await PreferencesServiceV3.get().setPreference<DataCollectionEmailPreferences>(
      PreferenceKey.dataCollectionEmail,
      defaultConfig,
      PreferenceScope.ORGANIZATION
    );
    setConfig(updatedConfig.value as DataCollectionEmailPreferences);
  }, [config]);

  return { config, saveConfig, resetConfig };
}

export function getDefaultFormConfig(): DataCollectionEmailPreferences {
  return {
    ccEmails: [],
    senderName: '',
    senderEmail: '',
    logoUrl: 'https://foresight-public-files.s3.amazonaws.com/preferences/menlovc/logo-kpi.png',
    primaryColor: '#FF8200',
    secondaryColor: '#000000',
    showHeader: false,
    showPendingRequestsCount: false,
    // No message customizations: the backend's template defaults apply. The form pre-fills
    // them via buildEffectiveConfig so admins see the text that will actually be sent.
    messages: {
      request: {},
      reminder: {},
    },
  };
}

const EMAIL_TEMPLATE_FIELDS = ['subject', 'header', 'body', 'contact', 'signature', 'footer'] as const;

function pickTemplateFields(message: EmailTemplateMessage & { cta?: string }): EmailTemplateMessage {
  return EMAIL_TEMPLATE_FIELDS.reduce<EmailTemplateMessage>((res, field) => {
    if (message[field] !== undefined) {
      res[field] = message[field];
    }
    return res;
  }, {});
}

/**
 * Merges the stored config over the backend template defaults so every form field shows the
 * text the email will actually contain. A field the admin explicitly cleared ('') stays empty;
 * only fields absent from the stored config fall back to the defaults.
 */
export function buildEffectiveConfig(
  config: DataCollectionEmailPreferences,
  templateDefaults: DataCollectionTemplateDefaultMessages | null
): DataCollectionEmailPreferences {
  const base = { ...getDefaultFormConfig(), ...config };
  if (!templateDefaults) {
    return base;
  }

  return {
    ...base,
    messages: {
      request: { ...pickTemplateFields(templateDefaults.request), ...config.messages?.request },
      reminder: { ...pickTemplateFields(templateDefaults.reminder), ...config.messages?.reminder },
    },
  };
}
